/**
 * Locale-aware pricing for GoGrabJob.
 *
 * Philosophy:
 *   - INR prices (India) are the base. Payment via Cashfree always processes in INR.
 *   - For other regions we show FIXED competitive local prices instead of exchange-rate
 *     conversions — converted prices look absurdly cheap (₹299 ≈ $3.56) and undermine
 *     perceived value. Fixed prices are benchmarked against regional competitors.
 *   - "es" = Spanish LatAm (USD), "es-ES" = Spain (EUR).
 *
 * Competitive benchmark (Feb 2026):
 *   Rezi $29/mo · Jobscan $49.95/mo · Kickresume $7-24/mo · Career Lab (RU) 500₽/mo
 *
 * Update prices periodically; check competitor pages every quarter.
 */

export type LocalePrice = {
  amount: number;
  currency: string;
  /** BCP-47 locale tag to use with Intl.NumberFormat */
  formatLocale: string;
};

/** Fixed competitive prices per plan per locale.
 *  Key = pack id from creditPacks.ts.  Fallback = INR. */
const PACK_LOCALE_PRICES: Record<string, Record<string, LocalePrice>> = {
  'pack-daypass': {
    en:      { amount: 29,   currency: 'INR', formatLocale: 'en-IN' },
    'en-IN': { amount: 29,   currency: 'INR', formatLocale: 'en-IN' },
    pt:      { amount: 4.9,  currency: 'BRL', formatLocale: 'pt-BR' },
    'pt-BR': { amount: 4.9,  currency: 'BRL', formatLocale: 'pt-BR' },
    ru:      { amount: 99,   currency: 'RUB', formatLocale: 'ru-RU' },
    'ru-RU': { amount: 99,   currency: 'RUB', formatLocale: 'ru-RU' },
    es:      { amount: 0.99, currency: 'USD', formatLocale: 'en-US' },
    'es-ES': { amount: 0.99, currency: 'EUR', formatLocale: 'es-ES' },
  },
  'pack-weekly': {
    en:      { amount: 99,   currency: 'INR', formatLocale: 'en-IN' },
    'en-IN': { amount: 99,   currency: 'INR', formatLocale: 'en-IN' },
    pt:      { amount: 19.9, currency: 'BRL', formatLocale: 'pt-BR' },
    'pt-BR': { amount: 19.9, currency: 'BRL', formatLocale: 'pt-BR' },
    ru:      { amount: 390,  currency: 'RUB', formatLocale: 'ru-RU' },
    'ru-RU': { amount: 390,  currency: 'RUB', formatLocale: 'ru-RU' },
    es:      { amount: 3.99, currency: 'USD', formatLocale: 'en-US' },
    'es-ES': { amount: 3.49, currency: 'EUR', formatLocale: 'es-ES' },
  },
  'pack-monthly': {
    en:      { amount: 349,  currency: 'INR', formatLocale: 'en-IN' },
    'en-IN': { amount: 349,  currency: 'INR', formatLocale: 'en-IN' },
    pt:      { amount: 64.9, currency: 'BRL', formatLocale: 'pt-BR' },
    'pt-BR': { amount: 64.9, currency: 'BRL', formatLocale: 'pt-BR' },
    ru:      { amount: 1190, currency: 'RUB', formatLocale: 'ru-RU' },
    'ru-RU': { amount: 1190, currency: 'RUB', formatLocale: 'ru-RU' },
    es:      { amount: 11.99, currency: 'USD', formatLocale: 'en-US' },
    'es-ES': { amount: 10.99, currency: 'EUR', formatLocale: 'es-ES' },
  },
  'pack-quarterly': {
    en:      { amount: 749,   currency: 'INR', formatLocale: 'en-IN' },
    'en-IN': { amount: 749,   currency: 'INR', formatLocale: 'en-IN' },
    pt:      { amount: 134.9, currency: 'BRL', formatLocale: 'pt-BR' },
    'pt-BR': { amount: 134.9, currency: 'BRL', formatLocale: 'pt-BR' },
    ru:      { amount: 2490,  currency: 'RUB', formatLocale: 'ru-RU' },
    'ru-RU': { amount: 2490,  currency: 'RUB', formatLocale: 'ru-RU' },
    es:      { amount: 24.99, currency: 'USD', formatLocale: 'en-US' },
    'es-ES': { amount: 22.99, currency: 'EUR', formatLocale: 'es-ES' },
  },
  'pack-halfyearly': {
    en:      { amount: 1499,  currency: 'INR', formatLocale: 'en-IN' },
    'en-IN': { amount: 1499,  currency: 'INR', formatLocale: 'en-IN' },
    pt:      { amount: 269.9, currency: 'BRL', formatLocale: 'pt-BR' },
    'pt-BR': { amount: 269.9, currency: 'BRL', formatLocale: 'pt-BR' },
    ru:      { amount: 4990,  currency: 'RUB', formatLocale: 'ru-RU' },
    'ru-RU': { amount: 4990,  currency: 'RUB', formatLocale: 'ru-RU' },
    es:      { amount: 44.99, currency: 'USD', formatLocale: 'en-US' },
    'es-ES': { amount: 41.99, currency: 'EUR', formatLocale: 'es-ES' },
  },
};

/** Fallback when pack ID is unknown or locale is unlisted. */
const FALLBACK_PRICES: Record<string, LocalePrice> = {
  'pack-daypass':    { amount: 29,   currency: 'INR', formatLocale: 'en-IN' },
  'pack-weekly':     { amount: 99,   currency: 'INR', formatLocale: 'en-IN' },
  'pack-monthly':    { amount: 349,  currency: 'INR', formatLocale: 'en-IN' },
  'pack-quarterly':  { amount: 749,  currency: 'INR', formatLocale: 'en-IN' },
  'pack-halfyearly': { amount: 1499, currency: 'INR', formatLocale: 'en-IN' },
};

function resolveLocaleKey(locale: string): string {
  // Try exact match first, then language-only prefix (e.g. 'pt-BR' → 'pt')
  const norm = locale.toLowerCase().trim();
  // Map common browser locale codes to our keys
  const aliases: Record<string, string> = {
    'en-us': 'en',
    'en-gb': 'en',
    'en-au': 'en',
    'pt-pt': 'pt',
    'es-mx': 'es',
    'es-ar': 'es',
    'es-co': 'es',
    'es-cl': 'es',
    'es-pe': 'es',
    'es-ve': 'es',
  };
  return aliases[norm] ?? norm.split('-')[0];
}

/**
 * Return the fixed competitive price for a pack in the user's locale.
 * Falls back to INR if locale or pack is unknown.
 */
export function getPackPrice(packId: string, locale: string): LocalePrice {
  const packPrices = PACK_LOCALE_PRICES[packId];
  if (!packPrices) return FALLBACK_PRICES[packId] ?? { amount: 0, currency: 'INR', formatLocale: 'en-IN' };

  const key = resolveLocaleKey(locale);
  // Try resolved key, then exact locale, then 'en' fallback
  return packPrices[key] ?? packPrices[locale] ?? packPrices['en'] ?? FALLBACK_PRICES[packId];
}

/**
 * Format a pack's competitive price for display in the user's locale.
 * Use this on pricing pages and modals instead of formatPriceForLocale.
 */
export function formatPackPrice(packId: string, locale: string): string {
  const { amount, currency, formatLocale } = getPackPrice(packId, locale);
  return formatLocaleAmount(amount, { amount, currency, formatLocale });
}

export function formatLocaleAmount(amount: number, localePrice: LocalePrice): string {
  const digits = amount < 10 ? 2 : 0;
  return new Intl.NumberFormat(localePrice.formatLocale, {
    style: 'currency',
    currency: localePrice.currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
}

export function formatZeroTax(locale: string): string {
  const lp = getPackPrice('pack-monthly', locale);
  return formatLocaleAmount(0, { amount: 0, currency: lp.currency, formatLocale: lp.formatLocale });
}

/**
 * Legacy helper — converts an INR amount using a fixed rate.
 * Kept for non-plan-specific use (e.g. PricingSection using old credit packs).
 * Prefer formatPackPrice for plan pricing.
 */
const LEGACY_RATES: Record<string, { currency: string; rate: number; formatLocale: string }> = {
  en:      { currency: 'INR', rate: 1,      formatLocale: 'en-IN' },
  'en-IN': { currency: 'INR', rate: 1,      formatLocale: 'en-IN' },
  pt:      { currency: 'BRL', rate: 0.063,  formatLocale: 'pt-BR' },
  'pt-BR': { currency: 'BRL', rate: 0.063,  formatLocale: 'pt-BR' },
  ru:      { currency: 'RUB', rate: 1.04,   formatLocale: 'ru-RU' },
  'ru-RU': { currency: 'RUB', rate: 1.04,   formatLocale: 'ru-RU' },
  es:      { currency: 'USD', rate: 0.012,  formatLocale: 'en-US' },
  'es-ES': { currency: 'EUR', rate: 0.011,  formatLocale: 'es-ES' },
};

export function formatPriceForLocale(amountInINR: number, locale: string): string {
  if (amountInINR === 0) return '0';
  const key = resolveLocaleKey(locale);
  const cfg = LEGACY_RATES[key] ?? LEGACY_RATES[locale] ?? LEGACY_RATES['en'];
  const amount = Math.round(amountInINR * cfg.rate);
  return new Intl.NumberFormat(cfg.formatLocale, {
    style: 'currency',
    currency: cfg.currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(amount);
}

/** Return the display currency code for a locale (e.g. 'INR', 'BRL'). */
export function getDisplayCurrencyCode(locale: string): string {
  const key = resolveLocaleKey(locale);
  return (LEGACY_RATES[key] ?? LEGACY_RATES['en']).currency;
}
